import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Typography
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

/**
 * Confirmación de una operación destructiva: advierte que no se puede
 * deshacer y exige marcar un checkbox antes de habilitar el botón. Mientras
 * `onConfirm` (async) corre, muestra un spinner con `progressMessage` y no
 * se puede cerrar ni volver a confirmar. Si `blockedReason` viene, explica
 * por qué no se puede borrar todavía y deja el botón deshabilitado.
 */
const ConfirmDestructiveDialog = ({
  open,
  onClose,
  onConfirm,
  title,
  children,
  confirmLabel = 'Eliminar',
  checkboxLabel = 'Entiendo que esta operación no se puede deshacer.',
  progressMessage = 'Eliminando…',
  blockedReason = null
}) => {
  const [acknowledged, setAcknowledged] = useState(false);
  const [isRunning, setIsRunning] = useState(false);

  // Cada apertura arranca sin confirmar.
  useEffect(() => {
    if (open) setAcknowledged(false);
  }, [open]);

  const handleClose = () => {
    if (!isRunning) onClose();
  };

  const handleConfirm = async () => {
    setIsRunning(true);
    try {
      await onConfirm();
    } finally {
      setIsRunning(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} disableEscapeKeyDown={isRunning} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <WarningAmberIcon color="error" />
        {title}
      </DialogTitle>
      <DialogContent>
        {children}
        {blockedReason ? (
          <Alert severity="info" sx={{ mt: 2 }}>{blockedReason}</Alert>
        ) : (
          <>
            <Alert severity="error" sx={{ mt: 2 }}>
              Esta operación no se puede deshacer.
            </Alert>
            <FormControlLabel
              sx={{ mt: 1 }}
              control={(
                <Checkbox
                  checked={acknowledged}
                  disabled={isRunning}
                  onChange={(event) => setAcknowledged(event.target.checked)}
                />
              )}
              label={checkboxLabel}
            />
          </>
        )}
        {isRunning && (
          <Box role="status" aria-live="polite" sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 1 }}>
            <CircularProgress size={20} />
            <Typography variant="body2">{progressMessage}</Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isRunning}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleConfirm}
          disabled={Boolean(blockedReason) || !acknowledged || isRunning}
          startIcon={isRunning ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {isRunning ? 'Eliminando…' : confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmDestructiveDialog;
